import { useEffect, useState } from "react";
import * as Yup from "yup";
import { AlertCircle, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import RHFWrapper from "@/components/RHFWrapper";
import RhfInput from "@/components/rhfFields/RhfInput";
import AxiosInstance from "@/utils/AxiosInstance";

export const ResetSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, "Must be at least 8 characters")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords do not match")
    .required("Confirm password is required"),
});

interface ResetFormValues {
  password: string;
  confirmPassword: string;
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [verifyingToken, setVerifyingToken] = useState(true);
  const [tokenError, setTokenError] = useState("");

  useEffect(() => {
    const verifyResetToken = async () => {
      if (!token) {
        setTokenError("This reset link is invalid.");
        setVerifyingToken(false);
        return;
      }

      try {
        await AxiosInstance().get("/auth/reset-password?token=" + token);
        setTokenError("");
      } catch (err: any) {
        setTokenError(
          err?.response?.data?.message || "This reset link is invalid or expired.",
        );
      } finally {
        setVerifyingToken(false);
      }
    };

    void verifyResetToken();
  }, [token]);

  const onSubmit = async (values: ResetFormValues) => {
    if (!token || tokenError) {
      toast.error(tokenError || "Invalid token");
      return;
    }

    try {
      setLoading(true);
      await AxiosInstance().post(
        "/auth/reset-password",
        { password: values.password },
        {
          headers: {
            Authorization: "Bearer " + token,
          },
        },
      );
      toast.success("Password reset successfully");
      navigate("/auth");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-[#F6F7FB] p-4">
      <Card className="w-full max-w-md border-0 shadow-sm">
        <CardHeader className="space-y-3 text-center">
          <img
            src="/logo.png"
            alt="Anthro Care"
            className="mx-auto h-12 w-auto cursor-pointer object-contain"
            onClick={() => navigate("/auth")}
          />
          <div className="space-y-1">
            <CardTitle className="text-xl font-semibold">Reset password</CardTitle>
            <CardDescription>
              {verifyingToken
                ? "Verifying your link"
                : tokenError
                  ? "Unable to continue"
                  : "Choose a new password for your account"}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="pb-8">
          {verifyingToken ? (
            <div className="flex flex-col items-center gap-2 py-10 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <p className="text-sm">Please wait…</p>
            </div>
          ) : tokenError ? (
            <div className="space-y-5">
              <div className="flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{tokenError}</span>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => navigate("/auth")}
              >
                Back to login
              </Button>
            </div>
          ) : (
            <RHFWrapper
              initialValues={{ password: "", confirmPassword: "" }}
              isValidate
              validateSchema={ResetSchema}
              onSubmit={(data) => onSubmit(data)}
              className="space-y-5"
            >
              <div className="space-y-1">
                <RhfInput
                  name="password"
                  label="New password"
                  required
                  type="password"
                  placeholder="Enter new password"
                />
                <p className="text-xs text-muted-foreground">Must be at least 8 characters</p>
              </div>
              <RhfInput
                name="confirmPassword"
                label="Confirm password"
                required
                type="password"
                placeholder="Re-enter new password"
              />
              <Button type="submit" size="lg" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving
                  </>
                ) : (
                  "Reset password"
                )}
              </Button>
              <Button
                type="button"
                variant="link"
                className="mx-auto flex h-auto items-center gap-1 p-0 text-muted-foreground"
                onClick={() => navigate("/auth")}
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to login
              </Button>
            </RHFWrapper>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
